.container {
  width: 100%;
  max-width: 1200px;
  height: 105px;
  padding: 18px map.get($spacing-scale, 'lg');
  background: var(--color-surface-primary);
  border-bottom: 1px solid var(--color-grey-soft);
  display: flex;
  align-items: center;
}

.content {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: map.get($spacing-scale, 'lg');
}

// Left side: Type info
.info {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 's-1h'); // 6px per Figma
  align-items: flex-start;
  flex: 1;
  min-width: 0; // Allows flex item to shrink below content size for text truncation
}

.titleRow {
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
}

.title {
  @include heading-4;
  line-height: 1.2;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.openInNewButton {
  flex-shrink: 0;
}

.changeBusinessTypeButton {
  flex-shrink: 0;
}

.slugRow {
  display: flex;
  align-items: center;
  gap: map.get($spacing-scale, 's-3'); // 12px per Figma
}

.slug {
  @include body-small-regular;
  color: var(--color-text-secondary);
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: map.get($spacing-scale, 's-3'); // 12px — matches `.slugRow` gap for consistent dot spacing
}

.businessTypeLink {
  @include body-small-regular;
  color: var(--color-text-secondary);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: transparent;
  text-underline-offset: 2px;

  @include transition(text-decoration-color, color);

  &:hover {
    color: var(--color-primary-base);
    text-decoration-color: var(--color-primary-base);
  }
}

.dot {
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background-color: var(--color-grey-dark);
  flex-shrink: 0;
}

.urlsCount {
  display: flex;
  align-items: center;
  gap: map.get($spacing-scale, 's-1'); // 4px per Figma
  @include body-small-regular;
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.platformIcon {
  height: 16px;
  flex-shrink: 0;
}

// Sequence selector
.sequenceSelector {
  position: relative;
}

.sequenceTag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 map.get($spacing-scale, 's-2');
  border-radius: map.get($radius, 'full');
  border: 1px solid var(--color-border);
  background: var(--color-surface-alt);
  cursor: pointer;
  @include metadata-small-medium;
  color: var(--color-text-tertiary);
  @include transition(background-color, border-color, color);

  &:hover {
    background: var(--color-grey-soft);
    border-color: var(--color-grey-neutral);
    color: var(--color-text-secondary);
  }
}

.sequenceTagSet {
  background: var(--color-semantic-container-accent);
  border-color: var(--color-semantic-border-accent);
  color: var(--color-primary-base);

  &:hover {
    background: var(--color-semantic-container-accent);
    border-color: var(--color-primary-base);
    color: var(--color-primary-base);
  }
}

.sequenceTagLabel {
  white-space: nowrap;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sequenceTagClear {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  opacity: 0.5;
  color: var(--color-primary-base);
  @include transition(opacity);

  &:hover {
    opacity: 1;
  }
}

.sequenceDropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 220px;
  z-index: 100;
  animation: popoverFadeIn 0.15s ease;
}

.date {
  @include metadata-small-regular;
  color: var(--color-text-tertiary);
  white-space: nowrap;
}

// Right side: Stats and chip
.stats {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  height: 100%;
  gap: map.get($spacing-scale, 'sm');
}

.topRow {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: map.get($spacing-scale, 's-3'); // 12px per Figma
}

.navButtons {
  display: flex;
  align-items: center;
  gap: map.get($spacing-scale, 's-1'); // 4px per Figma
}

.leads {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.leadsCount {
  @include heading-4;
  color: var(--color-text-primary);
}

.leadsLabel {
  @include heading-4;
  color: var(--color-text-tertiary);
}

.leadsLink {
  background-color: transparent;
  border: none;
  padding: map.get($spacing-scale, 's-1') map.get($spacing-scale, 's-2');
  border-radius: map.get($radius, 'md');
  cursor: pointer;

  @include transition(background-color);

  &:hover {
    background-color: var(--color-grey-light);
  }
}

// Chip row container (chip + optional edit button)
.chipRow {
  display: flex;
  align-items: center;
  gap: map.get($spacing-scale, 's-2'); // 8px gap between chip and edit button
}

.editButton {
  flex-shrink: 0;
}

.deleteButton {
  flex-shrink: 0;
}

// Chip
.chip {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--color-surface-alt);
  border-radius: 999px;
  cursor: pointer;

  @include transition(background-color);

  &:hover {
    background: var(--color-grey-light);
  }
}

.chipSection {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px map.get($spacing-scale, 'sm');
  white-space: nowrap;
}

.chipSectionBordered {
  border-left: 1px solid var(--color-border);
}

.chipValue {
  @include caption-medium;
  color: var(--color-text-primary);
}

.chipLabel {
  @include metadata-small-regular;
  color: var(--color-text-secondary);
}

// Popover
.popover {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 500px;
  background: var(--color-surface-glass);
  backdrop-filter: blur(10px);
  border-radius: map.get($radius, 'lg');
  box-shadow:
    0px 2px 4px 0px rgba(0, 0, 0, 0.05),
    0px 10px 30px 0px rgba(0, 0, 0, 0.15);
  z-index: 100;
  animation: popoverFadeIn 0.2s ease;

  [data-theme='dark'] & {
    background: rgba(42, 42, 42, 0.95);
  }
}

@keyframes popoverFadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.popoverContent {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'xl');
}

.popoverSection {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'md');
}

.popoverHeader {
  display: flex;
  align-items: center;
  gap: map.get($spacing-scale, 'xs');
}

.popoverHeaderText {
  @include body-bold;
  color: var(--color-text-secondary);
}

.popoverItems {
  display: flex;
  flex-direction: column;
  gap: map.get($spacing-scale, 'md');
}

.popoverItem {
  @include body-small-regular;
  background: var(--color-grey-light);
  padding: map.get($spacing-scale, 'sm') map.get($spacing-scale, 'md');
  border-radius: map.get($radius, 'md');
  color: var(--color-text-secondary);

  [data-theme='dark'] & {
    background: var(--color-surface-alt);
  }
}

// Responsive adjustments
@media (max-width: $breakpoint-md) {
  .container {
    height: auto;
    padding: map.get($spacing-scale, 'md');
  }

  .content {
    flex-direction: column;
    align-items: flex-start;
    gap: map.get($spacing-scale, 'md');
  }

  .stats {
    width: 100%;
    align-items: flex-start;
  }

  .popover {
    width: calc(100vw - 32px);
    max-width: 500px;
    right: auto;
    left: 0;
  }
}
